import {StockItem} from '../../../shared-utilities/models-old/datastructures';

/**
 * Identifiers that can refer to one shelf-talker row.
 * Firestore document id is `stockId`. The grid and local printed store also use `code`.
 *
 * @param item - Stock row or a previously stored printed row.
 * @returns Non-empty ids for that row.
 */
export function shelfTalkerItemIds(item: Pick<StockItem, 'code' | 'stockId'> | undefined): string[] {
  if (!item) {
    return [];
  }
  return [item.code, item.stockId].filter((id: string | undefined): id is string => !!id);
}

/**
 * Firestore document id for a shelf-talker stock write.
 *
 * @param item - Stock row being updated.
 * @returns `stockId` when present, otherwise `code`.
 */
export function shelfTalkerDocumentId(item: Pick<StockItem, 'code' | 'stockId'>): string {
  return item.stockId || item.code;
}

/**
 * Every id recorded on the printed map, including each stored row's code and stock id.
 *
 * @param printedById - Local printed rows keyed by whatever id was used when they were saved.
 * @returns Ids that count as already printed.
 */
export function printedShelfTalkerIds(printedById: Record<string, StockItem>): Set<string> {
  const ids = new Set<string>(Object.keys(printedById));
  Object.values(printedById).forEach((item: StockItem) => {
    shelfTalkerItemIds(item).forEach((id: string) => ids.add(id));
  });
  return ids;
}

/**
 * To Print is tagged stock that is not in the printed set. Printed is only that set.
 *
 * @param stockItems - Current shelf-talker stock rows.
 * @param printedById - Rows already printed for the store.
 * @returns The two tab lists.
 */
/**
 * Drops failed prints from the stored printed map so those rows can return to To Print.
 *
 * @param printedById - Current printed rows for the store.
 * @param failedItems - Rows whose tag removal did not succeed.
 * @returns Printed rows that should stay stored.
 */
export function withoutPrintedShelfTalkers(
  printedById: Record<string, StockItem>,
  failedItems: Record<string, StockItem>,
): Record<string, StockItem> {
  const failedIds = printedShelfTalkerIds(failedItems);
  return Object.entries(printedById).reduce((remaining: Record<string, StockItem>, [key, item]: [string, StockItem]) => {
    const ids = [key, ...shelfTalkerItemIds(item)];
    if (!ids.some((id: string) => failedIds.has(id))) {
      remaining[key] = item;
    }
    return remaining;
  }, {});
}

export function splitShelfTalkerPrintLists(
  stockItems: StockItem[],
  printedById: Record<string, StockItem>,
): {printed: StockItem[]; toPrint: StockItem[]} {
  const printedIds = printedShelfTalkerIds(printedById);
  return {
    printed: Object.values(printedById),
    toPrint: stockItems.filter((item: StockItem) => !shelfTalkerItemIds(item).some((id: string) => printedIds.has(id))),
  };
}
